import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { visibility } from "../../redux/slices/rightSideAd";
import { Tooltip } from "@mui/material";
import ToasterWithMessage from "../../components/toaster";
import TabsContentHeader, { useWindowWidth } from "../../components/AdminDashboardTabsContentHeader/TabsContentHeader";
import MobileScreenHeader from "../../components/MobileScreenHeader/MobileScreenHeader";
import MaterialActionsOverlay, { MenuItemType } from "../../components/ActionsOverlay/MaterialActionsOverlay";
import Loader from "../../components/Loader/Loader";
import images from "../../assets/images";
import Select, { StylesConfig, components } from "react-select";
import { debounce } from "lodash";
import moment from "moment";
import { GET_ALL_PAYMENT_ORDERS, UPDATE_PAYMENT_RECORD_STATUS, GET_ALL_PAYMENT_STATUS_TYPES, EXPORT_PAYMENT_ORDERS } from "./api/AdminPaymentsQuery";
import { GET_PROGRAMME_CATALOGUE } from "../AdminLoginPage/api/AdminLoginQuery";
import { getFormattedDateInfo, getScreenDimensions, parseAllowedStatuses } from "../../utils/helper";
import { SUCCESS_STATUS, VIRTUAL } from "../../utils/constants";
import "./AdminPayments.scss";

const SelectAny: any = Select;

interface StudentRegistrationNode {
  srCommunicationEmail: string | null;
  srCommunicationPhone: string | null;
  srGuardianName: string | null;
  srDetails?: any;
  classroomByCId?:{
    cCode : string;
  }
}

interface PaymentOrderNode {
  pmtPOrderId: string | null;
  pmtMOrderId: string | null;
  pmtId: string;
  prId: string;
  sId: string;
  createdAt:string;
  studentBySId: {
    userByUId: {
      uFullName: string | null;
      uFirstName: string | null;
      uEmailId: string | null;
      uCognitoId: string | null;
    } | null;
    studentRegistrationsBySId: {
      nodes: StudentRegistrationNode[];
    } | null;
  } | null;
  paymentStatusTypeByPstId: {
    pstName: string | null;
    pstId: string | null;
  } | null;
}

const CheckboxOption = (props: any) => {
  return (
    <components.Option {...props}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
        <input
          type="checkbox"
          checked={props.isSelected}
          readOnly
          style={{
            cursor: "pointer",
            width: "16px",
            height: "16px",
            accentColor: "var(--color-steelblue)",
            margin: 0
          }}
        />
        <span style={{ fontSize: "13px", fontFamily: "Inter", color: "var(--color-darkslategray)" }}>{props.label}</span>
      </div>
    </components.Option>
  );
};

const CustomValueContainer = ({ children, ...props }: any) => {
  const cleanChildren = React.Children.toArray(children).filter((child: any) => {
    return child?.type?.name !== "MultiValue" && child?.props?.components?.MultiValue === undefined;
  });

  return (
    <components.ValueContainer {...props}>
      <span className="filter-label-text" style={{ 
        color: "var(--color-steelblue)", 
        fontSize: "13px", 
        fontWeight: "500",
        fontFamily: "Inter",
        whiteSpace: "nowrap"
      }}>
        Status
      </span>
      <span className="filter-icon-symbol" style={{ alignItems: "center", justifyContent: "center" }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.5 2.5H14.5L9.5 8.5V13.5L6.5 15V8.5L1.5 2.5Z" stroke="var(--color-steelblue)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </span>
      {cleanChildren}
    </components.ValueContainer>
  );
};

const CustomDropdownIndicator = (props: any) => {
  const width = useWindowWidth();
  if (width <= 850 || props.selectProps?.isSearchActive) return null;
  return <components.DropdownIndicator {...props} />;
};


const AdminPayments: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { MoreIcon, bookIcon, greyArrow, UpArrow, DownArrow, searchCloseIcon } = images;
  const { success, error } = ToasterWithMessage();

  // Redux state for mobile header
  const role = useAppSelector((state) => state.auth.role);
  const schoolType = useAppSelector((state) => state.auth.schoolType);
  const profile = useAppSelector((state) => state.auth.adminProfile);
  const profileImage = useAppSelector((state) => state?.auth?.adminProfileImage);
  const studentName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;

  // Protect route for Virtual School Admin only
  useEffect(() => {
    if (schoolType !== VIRTUAL) {
      navigate("/admin-dashboard", { replace: true });
    }
  }, [schoolType, navigate]);

  // Ensure right sidebar ad is visible
  useEffect(() => {
    dispatch(visibility({ isVisible: true }));
  }, [dispatch]);

  // State
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [isSearchClick, setIsSearchClick] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string[]>(["ALL"]);
  const itemsPerPage = 20;

  // Table scroll and fades state
  const [scrollState, setScrollState] = useState({ left: false, right: false });
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Expanded Row State
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // Action Menu State & Constants
  const [actionAnchorEl, setActionAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedOrderForAction, setSelectedOrderForAction] = useState<PaymentOrderNode | null>(null);

  const handleActionClick = (e: React.MouseEvent<HTMLElement>, order: PaymentOrderNode) => {
    e.stopPropagation();
    setActionAnchorEl(e.currentTarget);
    setSelectedOrderForAction(order);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedOrderForAction(null);
  };

  const handleRowClick = (orderId: string) => {
    setExpandedOrderId(prev => prev === orderId ? null : orderId);
  };

  // Overflow arrays for tooltips
  const [isOrderOverflow, setIsOrderOverflow] = useState<boolean[]>([]);
  const [isStudentOverflow, setIsStudentOverflow] = useState<boolean[]>([]);
  const [isProgramOverflow, setIsProgramOverflow] = useState<boolean[]>([]);
  const [isEmailOverflow, setIsEmailOverflow] = useState<boolean[]>([]);

  // Refs for checking text overflow
  const orderRefs = useRef<(HTMLDivElement | null)[]>([]);
  const studentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const programRefs = useRef<(HTMLDivElement | null)[]>([]);
  const emailRefs = useRef<(HTMLDivElement | null)[]>([]);

  const filterObject = useMemo(() => {
    const searchInput = debouncedSearchValue.trim() ? `%${debouncedSearchValue.trim()}%` : "%";
    const baseFilter: any = {
      deletedAt: { isNull: true },
      or: [
        { studentBySId: { userByUId: { uFullName: { likeInsensitive: searchInput } } } },
        { studentBySId: { studentRegistrationsBySId: { every: { srCommunicationEmail: { likeInsensitive: searchInput } } } } },
        { pmtPOrderId: { likeInsensitive: searchInput } }
      ]
    };

    const includesAll = statusFilter.includes("ALL");
    if (includesAll) {
      return baseFilter;
    } else {
      const statusList = statusFilter.filter(s => s !== "ALL").map(s => s.toUpperCase());
      return {
        ...baseFilter,
        paymentStatusTypeByPstId: {
          pstName: {
            in: statusList
          }
        }
      };
    }
  }, [debouncedSearchValue, statusFilter]);

  // Queries & Mutations
  const { data: paymentData, loading: paymentLoading, refetch: refetchPayments } = useQuery(GET_ALL_PAYMENT_ORDERS, {
    fetchPolicy: "no-cache",
    variables: {
      first: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
      filter: filterObject
    },
    onError: (err) => {
      error(err?.message || "Failed to load payment orders.");
    }
  });

  const [updatePaymentRecordStatus, { loading: updatingStatus }] = useMutation(UPDATE_PAYMENT_RECORD_STATUS, {
    onCompleted: (data) => {
      const message = data?.updatePaymentRecordStatus?.message || "Payment status updated successfully!";
      success(message);
      setCurrentPage(1);
      refetchPayments();
    },
    onError: (err) => {
      error(err?.message || "Failed to update payment status.");
    }
  });

  const [exportPaymentOrders, { loading: isExporting }] = useMutation(EXPORT_PAYMENT_ORDERS, {
    onCompleted: (data) => {
      const exportResult = data?.exportPaymentOrders;
      if (exportResult?.downloadUrl) {
        try {
          const link = document.createElement("a");
          link.href = exportResult.downloadUrl;
          link.setAttribute("download", exportResult.fileName || "payment_orders.xlsx");
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => {
            success("Payment orders exported successfully!");
          }, 500);
        } catch {
          error("File download failed. Please try again!");
        }
      } else {
        error("Failed to generate export download link.");
      }
    },
    onError: (err) => {
      error(err?.message || "Failed to export payment orders.");
    }
  });

  const handleExportClick = () => {
    if (isExporting) return;
    const searchValueParam = debouncedSearchValue.trim() ? `%${debouncedSearchValue.trim()}%` : "%";
    const isAllStatus = statusFilter.includes("ALL");
    const statusesParam = isAllStatus || statusFilter.length === 0 
      ? [] 
      : statusFilter.filter(s => s !== "ALL").map(s => s.toUpperCase());

    exportPaymentOrders({
      variables: {
        search: searchValueParam,
        statuses: statusesParam
      }
    });
  };

  const handlePaymentStatusUpdate = useCallback((order: PaymentOrderNode, action: string) => {
    if (!order?.pmtId) return;
    updatePaymentRecordStatus({
      variables: {
        input: {
          pmtId: order.pmtId,
          action: action
        }
      }
    });
  }, [updatePaymentRecordStatus]);

  const { data: programmeData, loading: programmeLoading } = useQuery(GET_PROGRAMME_CATALOGUE, {
    fetchPolicy: "no-cache"
  });

  const { data: statusTypesData } = useQuery(GET_ALL_PAYMENT_STATUS_TYPES, {
    fetchPolicy: "no-cache"
  });

  const paymentOrders: PaymentOrderNode[] = useMemo(() => {
    return paymentData?.allPaymentOrders?.nodes || [];
  }, [paymentData]);

  const programmeList = useMemo(() => {
    return programmeData?.programmeCatalogue || [];
  }, [programmeData]);

  // Removed actions menu as table now expands inline

  // Tooltip overflow checks
  const checkOrderOverflow = (index: number) => {
    const el = orderRefs.current[index];
    if (el) {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsOrderOverflow(prev => {
        const next = [...prev];
        next[index] = overflow;
        return next;
      });
    }
  };

  const checkStudentOverflow = (index: number) => {
    const el = studentRefs.current[index];
    if (el) {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsStudentOverflow(prev => {
        const next = [...prev];
        next[index] = overflow;
        return next;
      });
    }
  };

  const checkProgramOverflow = (index: number) => {
    const el = programRefs.current[index];
    if (el) {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsProgramOverflow(prev => {
        const next = [...prev];
        next[index] = overflow;
        return next;
      });
    }
  };

  const checkEmailOverflow = (index: number) => {
    const el = emailRefs.current[index];
    if (el) {
      const overflow = el.scrollWidth > el.clientWidth;
      setIsEmailOverflow(prev => {
        const next = [...prev];
        next[index] = overflow;
        return next;
      });
    }
  };

  // Local filtering & searching on payment records
  const filteredOrders = paymentOrders;

  const handleSearchClick = () => {
    setIsSearchClick(true);
  };

  // Handle Search Input Change with 500ms debounce
  const handleSearchDebounce = useCallback(
    debounce((value: string) => {
      setDebouncedSearchValue(value);
      setCurrentPage(1); // Reset to page 1 on search
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      handleSearchDebounce.cancel();
    };
  }, [handleSearchDebounce]);

  const handleCloseSearch = () => {
    setIsSearchClick(false);
    setSearchValue("");
    setDebouncedSearchValue("");
    handleSearchDebounce.cancel();
    setCurrentPage(1);
  };

  const searchOnChange = (event: { target: { value: string } }) => {
    const val = event.target.value;
    setSearchValue(val);
    handleSearchDebounce(val);
  };

  // Pagination bounds & slice
  const totalPages = Math.ceil((paymentData?.allPaymentOrders?.totalCount || 0) / itemsPerPage) || 1;
  
  const paginatedOrders = paymentOrders;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const showMax = 5;

    if (totalPages <= showMax) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const formatPaymentsCount = (count: number) => {
    if (count === 0) return "No Payments";
    if (count === 1) return "1 Payment Order";
    return `${count} Payment Orders`;
  };

  const handleScroll = () => {
    const el = containerRef.current;
    if (el) {
      const left = el.scrollLeft > 5;
      const right = el.scrollLeft < el.scrollWidth - el.clientWidth - 5;
      setScrollState({ left, right });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleScroll();
    }, 100);

    window.addEventListener("resize", handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleScroll);
    };
  }, [paginatedOrders]);

  // Collapse expanded row when changing page, search query, or status filter
  useEffect(() => {
    setExpandedOrderId(null);
  }, [currentPage, debouncedSearchValue, statusFilter, isSearchClick]);

  const handleStatusFilterChange = (selected: any) => {
    const newValues: string[] = selected ? selected.map((opt: any) => opt.value) : [];
    const wasAllChecked = statusFilter.includes("ALL");
    const isAllCheckedNow = newValues.includes("ALL");

    if (isAllCheckedNow && !wasAllChecked) {
      // Case 1: "All Status" was just clicked/selected (clear other options)
      setStatusFilter(["ALL"]);
    } else if (!isAllCheckedNow && wasAllChecked && newValues.length === 0) {
      // Case 2: "All Status" was already checked, clicking it again does not uncheck
      setStatusFilter(["ALL"]);
    } else {
      // Case 3: Sub-options are active, so remove "ALL"
      const nextFilter = newValues.filter(val => val !== "ALL");
      if (nextFilter.length === 0) {
        setStatusFilter(["ALL"]);
      } else {
        setStatusFilter(nextFilter);
      }
    }
    setCurrentPage(1);
  };

  const formatStatusLabel = useCallback((name: string | null | undefined): string => {
    if (!name) return "";
    return name
      .toLowerCase()
      .split(" ")
      .map((word) => (word === "3ds" ? "3DS" : word.charAt(0).toUpperCase() + word.slice(1)))
      .join(" ");
  }, []);

  const statusOptions = useMemo(() => {
    const defaultOption = [{ value: "ALL", label: "All Status" }];
    const nodes = statusTypesData?.allPaymentStatusTypes?.nodes || [];
    const dynamicOptions = nodes.map((node: { pstId: string; pstName: string }) => {
      const name = node.pstName || "";
      return {
        value: name.toUpperCase(),
        label: formatStatusLabel(name)
      };
    });
    return [...defaultOption, ...dynamicOptions];
  }, [statusTypesData, formatStatusLabel]);

  const windowWidth = useWindowWidth();
  const isCompactFilter = windowWidth <= 850 || isSearchClick;

  const customSelectStyles: StylesConfig<any, true> = {
    control: (styles, { isFocused }) => ({
      ...styles,
      cursor: "pointer",
      borderRadius: "6px",
      width: isCompactFilter ? "34px" : "160px",
      minWidth: isCompactFilter ? "34px" : "160px",
      minHeight: isCompactFilter ? "34px" : "30px",
      height: isCompactFilter ? "34px" : "auto",
      padding: "0",
      border: "1px solid var(--color-steelblue)",
      boxShadow: isFocused ? "0 0 0 2px color-mix(in srgb, var(--color-steelblue) 15%, transparent)" : "none",
      backgroundColor: "var(--color-white)",
      fontFamily: "Inter",
      fontSize: "13px",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      justifyContent: isCompactFilter ? "center" : "space-between",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "var(--color-steelblue)",
        backgroundColor: "var(--color-softblue-hover)",
      }
    }),
    multiValue: (styles) => ({
      ...styles,
      display: "none"
    }),
    valueContainer: (styles) => ({
      ...styles,
      padding: isCompactFilter ? "0" : "0px 8px",
      display: "flex",
      alignItems: "center",
      justifyContent: isCompactFilter ? "center" : "flex-start",
      flexWrap: "nowrap",
      overflow: "hidden"
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: "none"
    }),
    indicatorsContainer: (styles) => ({
      ...styles,
      padding: isCompactFilter ? "0" : "0px 8px",
      display: isCompactFilter ? "none" : "flex"
    }),
    clearIndicator: (styles) => ({
      ...styles,
      padding: "0px"
    }),
    input: (styles) => ({
      ...styles,
      fontFamily: "Inter",
      fontWeight: "400",
      fontSize: "13px",
      margin: "0",
      padding: "0"
    }),
    placeholder: (styles) => ({
      ...styles,
      fontFamily: "Inter",
      fontWeight: "500",
      fontSize: "13px",
      color: "var(--color-darkgray)"
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isFocused ? "var(--color-softblue-hover)" : "var(--color-white)",
      color: "var(--color-darkslategray)",
      fontFamily: "Inter",
      fontWeight: isSelected ? "600" : "400",
      fontSize: "13px",
      cursor: "pointer",
      padding: "6px 12px",
      display: "flex",
      alignItems: "center",
      "&:active": {
        backgroundColor: "var(--color-softblue-hover)",
      }
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "var(--color-darkslategray)",
      fontFamily: "Inter",
      fontWeight: "400",
      fontSize: "13px"
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      color: "var(--color-steelblue)",
      padding: "0",
      display: isCompactFilter ? "none" : "flex",
      "&:hover": {
        color: "var(--color-steelblue)"
      }
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: "6px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
      border: "1px solid #e1e3e5",
      zIndex: 9999,
      minWidth: "160px",
      right: 0,
      left: "auto"
    }),
    menuList: (provided) => ({
      ...provided,
      padding: "4px 0"
    })
  };

  const isLoading = paymentLoading || programmeLoading || updatingStatus;

  if (schoolType !== VIRTUAL) {
    return null;
  }

  return (
    <div className="AdminPayments">
      {getScreenDimensions()?.isMobileScreen && isSearchClick ? (
        <div className="adminPaymentsSearchContainer" style={{ display: "flex", alignItems: "center", width: "100%", gap: "10px", flexWrap: "wrap" }}>
          <div className="adminPaymentsSearch" style={{ flex: "1 1 180px", minWidth: "180px" }}>
            <input
              id="searchBox"
              type="text"
              placeholder="Search"
              onChange={searchOnChange}
              value={searchValue}
              className="SearchInputFieldStyle"
            />
            <img
              src={searchCloseIcon}
              alt="closeIcon"
              className="closeIcon"
              onClick={handleCloseSearch}
            />
          </div>
          <div className="payment-toolbar">
            <div className="filter-group">
              <SelectAny
                classNamePrefix="react-select"
                isMulti
                isClearable={false}
                placeholder="Status"
                value={statusOptions.filter(opt => statusFilter.includes(opt.value))}
                onChange={handleStatusFilterChange}
                options={statusOptions}
                styles={customSelectStyles}
                isSearchable={false}
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                isSearchActive={isSearchClick}
                components={{
                  Option: CheckboxOption,
                  ValueContainer: CustomValueContainer,
                  DropdownIndicator: CustomDropdownIndicator,
                  Placeholder: () => null
                }}
              />
              <button 
                className={`export-btn ${isExporting ? "disabled" : ""}`} 
                type="button" 
                title="Export"
                onClick={handleExportClick}
                disabled={isExporting}
              >
                <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.5 10.5V12.5C2.5 13.0523 2.94772 13.5 3.5 13.5H12.5C13.0523 13.5 13.5 13.0523 13.5 12.5V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 2.5V9.5M8 9.5L5.5 7M8 9.5L10.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{isExporting ? "Exporting..." : "Export"}</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {getScreenDimensions()?.isMobileScreen && (
            <MobileScreenHeader
              profileImage={profileImage}
              role={role}
              studentName={studentName}
              profile={profile}
              handleSearchClick={handleSearchClick}
            />
          )}
          <TabsContentHeader
            title="Payment List"
            subtitle={formatPaymentsCount(paymentData?.allPaymentOrders?.totalCount || 0)}
            addLabel=""
            subImage={bookIcon}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            handleSearchDebounce={handleSearchDebounce}
            filterComponent={
              <div className="payment-toolbar">
                <div className="filter-group">
                  <SelectAny
                    classNamePrefix="react-select"
                    isMulti
                    isClearable={false}
                    placeholder="Status"
                    value={statusOptions.filter(opt => statusFilter.includes(opt.value))}
                    onChange={handleStatusFilterChange}
                    options={statusOptions}
                    styles={customSelectStyles}
                    isSearchable={false}
                    hideSelectedOptions={false}
                    closeMenuOnSelect={false}
                    isSearchActive={isSearchClick}
                    components={{
                      Option: CheckboxOption,
                      ValueContainer: CustomValueContainer,
                      DropdownIndicator: CustomDropdownIndicator,
                      Placeholder: () => null
                    }}
                  />
                  <button 
                    className={`export-btn ${isExporting ? "disabled" : ""}`} 
                    type="button" 
                    title="Export"
                    onClick={handleExportClick}
                    disabled={isExporting}
                  >
                    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2.5 10.5V12.5C2.5 13.0523 2.94772 13.5 3.5 13.5H12.5C13.0523 13.5 13.5 13.0523 13.5 12.5V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8 2.5V9.5M8 9.5L5.5 7M8 9.5L10.5 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{isExporting ? "Exporting..." : "Export"}</span>
                  </button>
                </div>
              </div>
            }
          />
        </>
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <div className={`table-scroll-wrapper ${scrollState.left && filteredOrders.length > 0 ? "show-left-fade" : ""} ${scrollState.right && filteredOrders.length > 0 ? "show-right-fade" : ""}`}>
          {filteredOrders.length > 0 ? (
            <>
              <div
                className={`programs-table-container ${filteredOrders.length === 0 ? "noData" : ""}`}
                ref={containerRef}
                onScroll={handleScroll}
              >
                <table className="programs-table">
                <thead>
                  <tr>
                    <th className="expand-header-cell"></th>
                    <th>Order ID</th>
                    <th>Student Name</th>
                    <th>Communication Email</th>
                    <th>Status</th>
                    <th className="more-header-cell"></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrders.map((order, index) => {
                    const orderId = order.pmtPOrderId || order.pmtMOrderId || "N/A";
                    const studentName = order.studentBySId?.userByUId?.uFullName || "-";
                    
                    const matchedProg = programmeList.find((p: any) => p.programmeId === order.prId);
                    const programName = matchedProg?.name || "Virtual Classroom Course";
                    
                    const communicationEmail = 
                      order.studentBySId?.studentRegistrationsBySId?.nodes?.[0]?.srCommunicationEmail || 
                      order.studentBySId?.userByUId?.uEmailId || 
                      "-";
                    
                    const phoneNumber = order.studentBySId?.studentRegistrationsBySId?.nodes?.[0]?.srCommunicationPhone || "-";
                    const statusName = order.paymentStatusTypeByPstId?.pstName || "-";
                    const isExpanded = expandedOrderId === order.pmtId;
                    const academicLevel = order.studentBySId?.studentRegistrationsBySId?.nodes?.[0]?.srDetails?.currentLevel || "-";
                    const paymentDate = order?.createdAt
                      ? `${getFormattedDateInfo(order.createdAt).formattedDate} ${moment(order.createdAt).format("YYYY")}`
                      : "-";
                    const isClassCodeEnabled = SUCCESS_STATUS.includes(statusName.toUpperCase());
                    const classCode = isClassCodeEnabled
                      ? order?.studentBySId?.studentRegistrationsBySId?.nodes?.[0]?.classroomByCId?.cCode || "-"
                      : "-";

                    return (
                      <React.Fragment key={order.pmtId || index}>
                        <tr 
                          onClick={() => handleRowClick(order.pmtId)}
                          className={`clickable-row ${isExpanded ? "expanded-active" : ""}`}
                          style={{ cursor: "pointer" }}
                        >
                          {/* Expand/Collapse Arrow Column (Front of the row) */}
                          <td className="expand-cell" onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(order.pmtId);
                          }}>
                            <img 
                              src={DownArrow} 
                              alt="toggle" 
                              className={`row-expand-arrow ${isExpanded ? "rotated" : ""}`} 
                            />
                          </td>

                          {/* Order ID */}
                          <td>
                            <Tooltip title={orderId} disableHoverListener={!isOrderOverflow[index]} placement="top">
                              <div
                                className="order-id-cell"
                                ref={(el) => { orderRefs.current[index] = el; }}
                                onMouseEnter={() => checkOrderOverflow(index)}
                              >
                                {orderId}
                              </div>
                            </Tooltip>
                          </td>

                          {/* Student Name */}
                          <td>
                            <Tooltip title={studentName} disableHoverListener={!isStudentOverflow[index]} placement="top">
                              <div
                                className="student-name-cell"
                                ref={(el) => { studentRefs.current[index] = el; }}
                                onMouseEnter={() => checkStudentOverflow(index)}
                              >
                                {studentName}
                              </div>
                            </Tooltip>
                          </td>

                          {/* Communication Email */}
                          <td>
                            <Tooltip title={communicationEmail} disableHoverListener={!isEmailOverflow[index]} placement="top">
                              <div
                                className="email-cell"
                                ref={(el) => { emailRefs.current[index] = el; }}
                                onMouseEnter={() => checkEmailOverflow(index)}
                              >
                                {communicationEmail}
                              </div>
                            </Tooltip>
                          </td>

                          {/* Payment Status */}
                          <td className="status-cell">
                            <span className={`status-badge ${statusName.toLowerCase().replace(/\s+/g, "-")}`}>
                              {statusName}
                            </span>
                          </td>

                          {/* More Action Button Column (End of the row) */}
                          <td className="actions-cell">
                            {(() => {
                              const enabledReconcileStatuses = parseAllowedStatuses(process.env.REACT_APP_ENABLE_RECONCILE_STATUSES);
                              const enabledRefundStatuses = parseAllowedStatuses(process.env.REACT_APP_ENABLE_REFUND_STATUSES);

                              const statusUpper = statusName.toUpperCase();
                              const isReconcileAllowed = enabledReconcileStatuses.includes(statusUpper);
                              const isRefundAllowed = enabledRefundStatuses.includes(statusUpper);

                              const hasAction = isReconcileAllowed || isRefundAllowed;
                              const isMoreDisabled = !hasAction;

                              return (
                                <div 
                                  className={`more-btn ${isMoreDisabled ? "disabled" : ""}`}
                                  onClick={(e) => {
                                    if (!isMoreDisabled) {
                                      handleActionClick(e, order);
                                    }
                                  }}
                                >
                                  <img src={MoreIcon} alt="more" className="more-icon" />
                                </div>
                              );
                            })()}
                          </td>
                        </tr>

                        <tr className={`expanded-row-details ${isExpanded ? "open" : ""}`}>
                          <td colSpan={6}>
                            <div className="expanded-wrapper">
                              <div className="expanded-content">
                                <div className="details-grid">
                                  <div className="details-column">
                                    <div className="detail-field">
                                      <span className="field-label">Communication Phone Number :</span>
                                      <span className="field-value">{phoneNumber}</span>
                                    </div>
                                    <div className="detail-field">
                                      <span className="field-label">Academic Level :</span>
                                      <span className="field-value">{academicLevel}</span>
                                    </div>
                                  </div>

                                  <div className="details-column">
                                    <div className="detail-field">
                                      <span className="field-label">Program Name :</span>
                                      <span className="field-value">{programName}</span>
                                    </div>
                                     <div className="detail-field">
                                      <span className="field-label"> Payment Date :</span>
                                      <span className="field-value">{paymentDate}</span>
                                    </div>
                                  </div>
                                  <div className="details-column">
                                    <div className="detail-field">
                                      <span className="field-label">Class Code :</span>
                                      <span className="field-value">{classCode}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
              <div className="pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={currentPage === 1 ? "disableprevBtn" : "prevBtn"}
                >
                  <img src={greyArrow} alt="prev" className="paginationArrow" />
                </button>
                <div className="pageNumbers">
                  {getPageNumbers().map((page, idx) => {
                    if (page === "...") {
                      return (
                        <span key={`ellipsis-${idx}`} className="paginationEllipsis">
                          ...
                        </span>
                      );
                    }
                    return (
                      <button
                        key={`page-${page}`}
                        onClick={() => setCurrentPage(Number(page))}
                        className={`pageBtn ${currentPage === page ? "activePageBtn" : ""}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  className={currentPage >= totalPages ? "disablenextBtn" : "nextBtn"}
                >
                  <img src={greyArrow} alt="next" className="paginationArrow nextArrow" />
                </button>
              </div>
            </>
          ) : (
            <div className="noDataFoundStyle">No data found</div>
          )}
        </div>
      )}

      <MaterialActionsOverlay
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        handleClose={handleActionClose}
        menuItems={
          selectedOrderForAction
            ? (() => {
                const statusName = (selectedOrderForAction.paymentStatusTypeByPstId?.pstName || "").toUpperCase();
                const enabledReconcileStatuses = parseAllowedStatuses(process.env.REACT_APP_ENABLE_RECONCILE_STATUSES);
                const enabledRefundStatuses = parseAllowedStatuses(process.env.REACT_APP_ENABLE_REFUND_STATUSES);

                const items = [];

                if (enabledReconcileStatuses.includes(statusName)) {
                  items.push({
                    label: "Verify & Reconcile",
                    onClick: () => {
                      const orderToUpdate = selectedOrderForAction;
                      handleActionClose();
                      if (orderToUpdate) {
                        handlePaymentStatusUpdate(orderToUpdate, "VERIFY");
                      }
                    }
                  });
                }

                if (enabledRefundStatuses.includes(statusName)) {
                  items.push({
                    label: "Mark as Refund",
                    onClick: () => {
                      const orderToUpdate = selectedOrderForAction;
                      handleActionClose();
                      if (orderToUpdate) {
                        handlePaymentStatusUpdate(orderToUpdate, "REFUND");
                      }
                    }
                  });
                }

                return items;
              })()
            : []
        }
        actionsHeight="auto"
        position="left"
        verticalPosition="bottom"
      />
    </div>
  );
};

export default AdminPayments;
